import * as Sentry from "@sentry/react";
import styled from "styled-components/macro";
import SVG from "react-inlinesvg/esm";
import { useState } from "react";
import TextareaAutosize from "react-textarea-autosize";
import Auth from "../../../../Auth";
import { useStructuredOutlineStore } from "../StructuredOutlineStore";
import GoButton from "../../../GoButton";
import classNames from "classnames";
import { useSeriesStore } from "../../../../stores/seriesStore";
import { useToast } from "../../../ToastWrapper";
import { AppEventPublisher, StoryBibleAction, StoryBibleType } from "../../../../AppEventPublisher";
import { SudoButton } from "src/sudokit/components/SudoButton";
import { useSudoLayoutEventEmitter } from "src/lib/layout-events";
import { usePersistentChatStore } from "src/stores/persistentChatStore";
import { isStoryBibleCtaBlockingRetrigger } from "src/components/PersistentChat/storyBibleCta";
import { useSudowriteStore } from "../../../../stores/sudowriteStore";

const Container = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  background-color: var(--menu-color-fullscreen);
  border-radius: 20px;
  box-shadow: var(--tiny-drop-shadow-head-on);
  margin: 0px 30px;
  margin-bottom: 20px;
  align-items: flex-start;
  padding: 14px;
`;

const TopContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 20px;
  height: 66px;
  &.rewrite-normal {
    height: 52px;
  }
`;

const StyledImage = styled.img`
  display: flex;
  flex-direction: row;
  align-items: center;
  height: 62px;
  width: 69px;
  position: relative;
  bottom: 14px;
  &.rewrite-generated {
    height: 84px;
    width: 92px;
  }
`;

const TitleText = styled.div`
  display: flex;
  flex-direction: column;
  font-size: 20px;
  font-weight: 500;
  margin-bottom: 14px;
  margin-left: -10px;
  p {
    line-height: 130% !important;
    color: var(--grey-0);
  }
`;

const BottomContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: ${(props) => (props.$postGeneration ? "center" : "flex-end")};
  justify-content: space-between;
  gap: 10px;
  font-size: 16px;
  font-family: "Inter";
  color: var(--grey-0);

  /* Only show box styling for normal rewrite mode, not post-generation */
  ${(props) => !props.$postGeneration && `
    border-radius: 5px;
    border: 1px solid var(--grey-3);
    box-shadow: var(--tiny-drop-shadow);
    padding: 8px 10px 4px 10px;
    width: calc(100% - 20px);

    &:focus-within {
      border: 1px solid var(--primary-cta);
    }
  `}

  ${(props) => props.$postGeneration && `
    width: 100%;
    padding: 0;
  `}

  textarea {
    height: 49px;
    border: none;
    min-height: 49px;
    font-size: 16px;
    padding: unset;
    margin: unset;
    border-radius: 5px;
    background: var(--menu-color-fullscreen);
    resize: none;
    font-size: 16px;
    font-family: "Inter";
    width: 100%;
    color: var(--grey-0);
    &::placeholder {
      color: var(--grey-2);
    }
  }

  .go-button {
    margin-bottom: 6px;
  }
`;

const PostGenerationContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  gap: 12px;
  flex-wrap: wrap;
`;

const PostGenerationText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1;

  p {
    margin: 0;
    font-size: 16px;
    line-height: 130%;
    color: var(--grey-0);
  }

  span {
    font-size: 14px;
    color: var(--grey-2);
  }
`;

const CloseButton = styled.div`
  position: absolute;
  top: 10px;
  right: 10px;
  cursor: pointer;
  svg {
    path {
      fill: var(--grey-0);
    }
  }
`;

function RewriteOutline({ projectId, rewriteOutlineTextAreaRef: textAreaRef }) {
  const setOutline = useStructuredOutlineStore((state) => state.setOutline);
  const setReadOnly = useStructuredOutlineStore((state) => state.setReadOnly);
  const setRewriteOpen = useStructuredOutlineStore((state) => state.setRewriteOpen);
  const getPromptRepresentation = useStructuredOutlineStore((state) => state.getPromptRepresentation);
  const generationFramework = useStructuredOutlineStore((state) => state.outline.content.generationFramework);
  const showGoToChat = useStructuredOutlineStore((state) => state.outline.content?.showGoToChat ?? false);
  const [instructions, setInstructions] = useState("");
  const [generating, setGenerating] = useState(false);
  const selectedProjects = useSeriesStore((state) => state.selectedProjects);
  const { setToast } = useToast();
  const emitLayoutEvent = useSudoLayoutEventEmitter();
  const hasAccessToAgenticChat = useSudowriteStore((state) => state.subscriptionJSON?.hasAccessToAgenticChat);
  const globalCtaActive = usePersistentChatStore((state) =>
    isStoryBibleCtaBlockingRetrigger(state.ctaAction),
  );

  const projectToUpdate = selectedProjects.outline?.projectId || projectId;
  const isPostGeneration = showGoToChat;

  const handleClose = () => {
    setRewriteOpen(false);
  };

  const triggerRewriteOutlineChat = () => {
    emitLayoutEvent("trigger_cta_chat", {
      type: "rewrite_outline",
      displayTitle: "Rewrite Outline",
      userMessage: "Rewrite Outline",
      cannedResponse: "Ok, let's do it!",
    });
  };

  const handleRewrite = async () => {
    const trimmedInstructions = instructions.trim();
    if (!trimmedInstructions) return;

    try {
      const body = {
        projectId: projectToUpdate,
        outline: getPromptRepresentation(),
        feedback: trimmedInstructions,
        generationFramework,
      };

      setGenerating(true);
      setReadOnly(true);
      const result = await Auth.fetchWithIdToken("/api/ai/storyEngine/rewrite/structured-outline", body);
      AppEventPublisher.storyBible.edited(StoryBibleType.Outline, StoryBibleAction.Rewrite);
      if (result.success) {
        setOutline(result.outline);
        setInstructions("");
      } else {
        setToast("Could not rewrite outline. Please try again.");
      }
    } catch (e) {
      Sentry.captureException(e, { tags: { feature: "structured_outline_rewrite" } });
      setToast("Unexpected Problem while rewriting outline. Please try again.");
    } finally {
      setGenerating(false);
      setReadOnly(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      handleRewrite();
    }
    if (e.key === "Escape") {
      handleClose();
    }
  };

  const buttonDisabled = instructions.trim().length === 0;
  const handleGoToChat = () => {
    if (hasAccessToAgenticChat) {
      if (globalCtaActive) return;
      triggerRewriteOutlineChat();
      return;
    }
    emitLayoutEvent("focus_chat", null);
  };

  return (
    <Container>
      <CloseButton
        role="button"
        tabIndex="0"
        onClick={handleClose}
        aria-label="Close Rewrite Outline"
      >
        <SVG src="/assets/close.svg" />
      </CloseButton>
      <TopContainer className={classNames({ "rewrite-normal": !isPostGeneration })}>
        <StyledImage
          src="/assets/StructuredOutlineRewrite.png"
          alt="Outline"
          className={classNames({ "rewrite-generated": isPostGeneration })}
        />
        <TitleText>
          {!isPostGeneration && <p>Rewrite Outline</p>}
          {isPostGeneration && (
            <p>
              Want to smart rewrite your outline?
            </p>
          )}
        </TitleText>
      </TopContainer>
      <BottomContainer $postGeneration={isPostGeneration}>
        {!isPostGeneration && (
          <>
            <TextareaAutosize
              ref={textAreaRef}
              placeholder="e.g. “Add three chapters exploring Joan’s Arc” or &#10;”rewrite Act 4 to have more of a clock leading up to the climax”"
              value={instructions}
              onChange={(e) => setInstructions(e.target.value)}
              onKeyDown={handleKeyPress}
              disabled={generating}
              className="textarea-autosize"
            />
            <GoButton
              className="go-button"
              onClick={() => handleRewrite()}
              progressLoading={generating}
              disabled={buttonDisabled}
            />
          </>
        )}
        {isPostGeneration && (
          <PostGenerationContent>
            <PostGenerationText>
              <p>AI will help you iterate!</p>
              <span>Refine, expand, or rework any part of your outline.</span>
            </PostGenerationText>
            <SudoButton
              variant="primary"
              onClick={handleGoToChat}
              disabled={hasAccessToAgenticChat && globalCtaActive}
            >
              Continue in Chat
            </SudoButton>
          </PostGenerationContent>
        )}
      </BottomContainer>
    </Container>
  );
}

export default RewriteOutline;
